import React, { useEffect, useState } from "react";
import { Contact, Phone, Mail, StickyNote, Trash2, Pencil, Plus, Loader2, MessageSquare, Send, X, PhoneOutgoing, Upload, FileText, Clock } from "lucide-react";
import { contacts as contactsApi, twilio } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", notes: "" };

// Parse pasted text or CSV into contact rows.
// Accepts comma- or tab-separated: name, phone, email, notes (extra cells fold into notes).
function parseRows(text) {
  const lines = (text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows = [];
  lines.forEach((line, i) => {
    const cells = line.split(/[,\t]/).map((c) => c.trim());
    if (i === 0 && (cells[0] || "").toLowerCase() === "name") return; // skip header
    const [name, phone, email, ...rest] = cells;
    if (!name) return;
    rows.push({ name, phone: phone || "", email: email || "", notes: rest.join(", ") });
  });
  return rows;
}

export default function Contacts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null); // contact id when editing
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sms, setSms] = useState(null); // contact object being texted
  const [smsText, setSmsText] = useState("");
  const [sending, setSending] = useState(false);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await contactsApi.list(); setItems(d.items || []); }
    catch (_) { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setEditing(null); setShowForm(true); };
  const openEdit = (c) => { setForm({ name: c.name || "", phone: c.phone || "", email: c.email || "", notes: c.notes || "" }); setEditing(c.id); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing) { await contactsApi.update(editing, form); toast.success("Contact updated"); }
      else { await contactsApi.add(form.name.trim(), form.phone.trim(), form.email.trim(), form.notes.trim()); toast.success("Contact saved"); }
      setShowForm(false); setForm(empty); setEditing(null); load();
    } catch (e) {
      toast.error("Couldn't save", { description: e?.response?.data?.detail || "Try again" });
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name} from your contacts?`)) return;
    try { await contactsApi.remove(c.id); setItems((x) => x.filter((i) => i.id !== c.id)); toast.success("Contact removed"); }
    catch (e) { toast.error("Couldn't remove", { description: e?.response?.data?.detail || "" }); }
  };

  const openSms = (c) => { setSms(c); setSmsText(""); setScheduleOn(false); setScheduleAt(""); };
  const sendSms = async () => {
    if (!smsText.trim()) { toast.error("Type a message first"); return; }
    if (scheduleOn && !scheduleAt) { toast.error("Pick a date and time to schedule"); return; }
    setSending(true);
    try {
      if (scheduleOn) {
        const iso = new Date(scheduleAt).toISOString();
        const r = await twilio.scheduleSms(sms.phone, sms.name, smsText.trim(), iso);
        if (r.ok) { toast.success(`Text scheduled for ${sms.name}`, { description: r.when_local }); setSms(null); setSmsText(""); }
        else { toast.error("Couldn't schedule", { description: r.error || "" }); }
      } else {
        const r = await twilio.sendSms(sms.phone, sms.name, smsText.trim());
        if (r.ok) { toast.success(`Text sent to ${r.name || sms.name}`); setSms(null); setSmsText(""); }
        else { toast.error("Couldn't send", { description: r.error || "" }); }
      }
    } catch (e) {
      toast.error(scheduleOn ? "Couldn't schedule" : "Couldn't send", { description: e?.response?.data?.detail || "Try again" });
    } finally { setSending(false); }
  };

  const importRows = parseRows(importText);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const text = await file.text(); setImportText((prev) => (prev ? prev + "\n" : "") + text); }
    catch (_) { toast.error("Couldn't read that file"); }
    e.target.value = "";
  };
  const doImport = async () => {
    if (importRows.length === 0) { toast.error("Nothing to import — paste a list or choose a CSV first"); return; }
    setImporting(true);
    try {
      const r = await contactsApi.bulk(importRows);
      toast.success(`Imported ${r.added} contact${r.added === 1 ? "" : "s"}`, { description: r.skipped ? `${r.skipped} skipped (duplicates or missing name)` : undefined });
      setShowImport(false); setImportText(""); load();
    } catch (e) {
      toast.error("Import failed", { description: e?.response?.data?.detail || "Try again" });
    } finally { setImporting(false); }
  };

  return (
    <div data-testid="contacts-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-semibold text-white sm:text-4xl">
            <Contact className="text-[#00E5FF]" size={30} /> Contacts
          </h1>
          <p className="mt-1 text-sm text-slate-400">Save people so you can tell an associate to "call" or "text" them by name — no number needed.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={() => { setImportText(""); setShowImport(true); }} className="flex h-11 items-center gap-2 rounded-xl border border-white/12 px-4 text-sm font-medium text-slate-200 transition-colors hover:border-[#00E5FF]/50 hover:text-white" data-testid="contact-import-open-btn">
              <Upload size={16} /> Import
            </button>
            <button onClick={openAdd} className="flex h-11 items-center gap-2 rounded-xl bg-[#00E5FF] px-5 text-sm font-semibold text-[#030A16] transition-transform hover:scale-[1.02]" data-testid="contact-add-open-btn">
              <Plus size={16} /> Add contact
            </button>
          </div>
        )}
      </div>

      {/* List */}
      <div className="max-w-3xl space-y-2" data-testid="contacts-list">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="animate-spin" size={15} /> Loading contacts…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-white/8 bg-[#030A16]/40 p-6 text-center text-sm text-slate-500">No contacts yet.{isAdmin ? " Add one to let associates call or text them by name." : ""}</div>
        ) : items.map((c) => (
          <div key={c.id} className="flex items-start gap-3 rounded-xl border border-white/8 bg-[#030A16]/40 p-3.5" data-testid={`contact-row-${c.id}`}>
            <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] text-sm font-semibold text-[#030A16]">
              {(c.name || "?").trim().charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white">{c.name}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[12px] text-slate-400">
                {c.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {c.phone}</span>}
                {c.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {c.email}</span>}
              </div>
              {c.notes && <div className="mt-1 flex items-start gap-1 text-[12px] text-slate-500"><StickyNote size={11} className="mt-0.5 shrink-0" /><span className="whitespace-pre-wrap">{c.notes}</span></div>}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button onClick={() => openSms(c)} disabled={!c.phone} title="Send a text" className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-[#00E5FF]/10 hover:text-[#00E5FF] disabled:cursor-not-allowed disabled:opacity-30" data-testid={`contact-text-${c.id}`}>
                <MessageSquare size={15} />
              </button>
              {isAdmin && (
                <>
                  <button onClick={() => openEdit(c)} title="Edit" className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white" data-testid={`contact-edit-${c.id}`}>
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => remove(c)} title="Delete" className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-[#FF0055]/10 hover:text-[#FF3B5C]" data-testid={`contact-delete-${c.id}`}>
                    <Trash2 size={15} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#050D1C] p-6" onClick={(e) => e.stopPropagation()} data-testid="contact-form-modal">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">{editing ? "Edit contact" : "Add contact"}</h2>
              <button onClick={() => setShowForm(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" data-testid="contact-form-close"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="h-11 w-full rounded-xl border border-white/10 bg-[#030A16]/60 px-3 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50" data-testid="contact-name-input" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (e.g. +14705551234)" className="h-11 w-full rounded-xl border border-white/10 bg-[#030A16]/60 px-3 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50" data-testid="contact-phone-input" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="h-11 w-full rounded-xl border border-white/10 bg-[#030A16]/60 px-3 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50" data-testid="contact-email-input" />
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (e.g. VIP customer, prefers texts) — shared with the associate for context" rows={3} className="w-full rounded-xl border border-white/10 bg-[#030A16]/60 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50" data-testid="contact-notes-input" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="h-10 rounded-xl border border-white/10 px-4 text-sm text-slate-300 hover:text-white">Cancel</button>
              <button onClick={save} disabled={busy} className="h-10 rounded-xl bg-[#00E5FF] px-5 text-sm font-semibold text-[#030A16] transition-transform hover:scale-[1.02] disabled:opacity-50" data-testid="contact-save-btn">
                {busy ? <Loader2 className="mx-auto animate-spin" size={16} /> : editing ? "Save changes" : "Add contact"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send SMS modal */}
      {sms && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setSms(null)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#050D1C] p-6" onClick={(e) => e.stopPropagation()} data-testid="contact-sms-modal">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-white"><MessageSquare className="text-[#00E5FF]" size={18} /> Text {sms.name}</h2>
              <button onClick={() => setSms(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" data-testid="contact-sms-close"><X size={18} /></button>
            </div>
            <p className="mb-3 text-[12px] text-slate-500">Sent from your ABC Services number to {sms.phone}.</p>
            <textarea value={smsText} onChange={(e) => setSmsText(e.target.value)} placeholder="Type your message… (reminder, follow-up, confirmation)" rows={4} className="w-full rounded-xl border border-white/10 bg-[#030A16]/60 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50" data-testid="contact-sms-input" />
            <div className="mt-3 rounded-xl border border-white/8 bg-[#030A16]/40 p-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-200" data-testid="contact-sms-schedule-toggle">
                <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} className="h-4 w-4 accent-[#00E5FF]" />
                <Clock size={14} className="text-[#00E5FF]" /> Schedule for later
              </label>
              {scheduleOn && (
                <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} min={new Date(Date.now() + 60000).toISOString().slice(0, 16)} className="mt-2 h-10 w-full rounded-lg border border-white/10 bg-[#030A16]/60 px-3 text-sm text-white focus:border-[#00E5FF]/50" data-testid="contact-sms-schedule-input" />
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setSms(null)} className="h-10 rounded-xl border border-white/10 px-4 text-sm text-slate-300 hover:text-white">Cancel</button>
              <button onClick={sendSms} disabled={sending} className="flex h-10 items-center gap-2 rounded-xl bg-[#00E5FF] px-5 text-sm font-semibold text-[#030A16] transition-transform hover:scale-[1.02] disabled:opacity-50" data-testid="contact-sms-send-btn">
                {sending ? <Loader2 className="animate-spin" size={16} /> : scheduleOn ? <Clock size={15} /> : <Send size={15} />} {scheduleOn ? "Schedule text" : "Send text"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk import modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowImport(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#050D1C] p-6" onClick={(e) => e.stopPropagation()} data-testid="contact-import-modal">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-white"><Upload className="text-[#00E5FF]" size={18} /> Import contacts</h2>
              <button onClick={() => setShowImport(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" data-testid="contact-import-close"><X size={18} /></button>
            </div>
            <p className="mb-3 text-[12px] text-slate-500">Paste one contact per line as <span className="font-mono text-slate-400">Name, Phone, Email, Notes</span> — or choose a CSV file. A header row is skipped automatically. Duplicates (same name or number) are ignored.</p>
            <div className="mb-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-white/12 px-3 py-2 text-xs text-slate-200 hover:border-[#00E5FF]/50 hover:text-white" data-testid="contact-import-file-label">
                <FileText size={14} /> Choose CSV file
                <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" data-testid="contact-import-file" />
              </label>
            </div>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={"John Carter, +14705551234, john@example.com, VIP customer\nMaria Lopez, +14045557788, , Follow up about renewal"}
              rows={7}
              className="w-full rounded-xl border border-white/10 bg-[#030A16]/60 px-3 py-2 font-mono text-[12px] text-white placeholder:text-slate-600 focus:border-[#00E5FF]/50"
              data-testid="contact-import-input"
            />
            <div className="mt-2 text-[12px] text-slate-400" data-testid="contact-import-count">
              {importRows.length > 0 ? `${importRows.length} contact${importRows.length === 1 ? "" : "s"} ready to import` : "Nothing to import yet"}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowImport(false)} className="h-10 rounded-xl border border-white/10 px-4 text-sm text-slate-300 hover:text-white">Cancel</button>
              <button onClick={doImport} disabled={importing || importRows.length === 0} className="flex h-10 items-center gap-2 rounded-xl bg-[#00E5FF] px-5 text-sm font-semibold text-[#030A16] transition-transform hover:scale-[1.02] disabled:opacity-50" data-testid="contact-import-btn">
                {importing ? <Loader2 className="animate-spin" size={16} /> : <Upload size={15} />} Import {importRows.length > 0 ? importRows.length : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
